import {
  Dispatch,
  SetStateAction,
  createContext,
  useEffect,
  useState,
} from 'react';
import { IKeyAnyValue } from 'common/types';
import { IAirportDetail } from 'hooks/useAirportDetailList';
import { DepartureTime } from 'list/common/filter/DepartureTimeFilter';
import {
  IFilterStore,
  initialFilterStore,
  loadFilterStore,
} from 'list/international/filter/filterStore';
import RangeFilter from 'list/international/filter/rangeFilter';
import { InternationalFilterTab } from 'list/international/filter/types';
import { IQueryValue } from 'list/international/types';

export enum FilterKeys {
  airline = 'airline',
  airport = 'airport',
  alliance = 'alliance',
  carbonEmissionOnly = 'carbonEmissionOnly',
  departureTime = 'departureTime',
  duration = 'duration',
  excludeCard = 'excludeCard',
  fareType = 'fareType',
  price = 'price',
  sameAirlineOnly = 'sameAirlineOnly',
  sameAirportOnly = 'sameAirportOnly',
  travelClubOnly = 'travelClubOnly',
  viaCount = 'viaCount',
}

interface IFilterState {
  [FilterKeys.airline]: Set<string>;
  [FilterKeys.airport]: Set<string>[][];
  [FilterKeys.alliance]: boolean[];
  [FilterKeys.carbonEmissionOnly]: boolean;
  [FilterKeys.departureTime]: Set<DepartureTime>[];
  [FilterKeys.duration]: RangeFilter[];
  [FilterKeys.excludeCard]: boolean;
  [FilterKeys.fareType]: Set<string>;
  [FilterKeys.price]: RangeFilter;
  [FilterKeys.sameAirlineOnly]: boolean;
  [FilterKeys.sameAirportOnly]: boolean;
  [FilterKeys.travelClubOnly]: boolean;
  [FilterKeys.viaCount]: Set<number>;
}

interface IInternationalFilterContextType {
  activeTab?: InternationalFilterTab;
  currentItinSeq: number;
  filterModal: boolean;
  filterState: IFilterState;
  filterStore: IFilterStore; // 필터 데이터
  isStorageUsed: boolean;
  setActiveTab: Dispatch<SetStateAction<InternationalFilterTab | undefined>>;
  setCurItinSeq: Dispatch<SetStateAction<number>>;
  setFilterModal: Dispatch<SetStateAction<boolean>>;
  setFilterState: (key: FilterKeys, val: any) => void;
  setFilterStates: (states: IKeyAnyValue) => void;
  setFilterStore: Dispatch<SetStateAction<IFilterStore>>;
  updateSelected: Dispatch<SetStateAction<void>>;
}

const initialFilterState: IFilterState = {
  [FilterKeys.airline]: new Set<string>(),
  [FilterKeys.airport]: [],
  [FilterKeys.alliance]: [],
  [FilterKeys.carbonEmissionOnly]: false,
  [FilterKeys.departureTime]: [],
  [FilterKeys.duration]: [],
  [FilterKeys.excludeCard]: false,
  [FilterKeys.fareType]: new Set<string>(),
  [FilterKeys.price]: new RangeFilter(0, 0, 0, 0),
  [FilterKeys.sameAirlineOnly]: false,
  [FilterKeys.sameAirportOnly]: false,
  [FilterKeys.travelClubOnly]: false,
  [FilterKeys.viaCount]: new Set<number>(),
};

const emptyFunc = () => {};

export const InternationalFilterContext =
  createContext<IInternationalFilterContextType>({
    activeTab: undefined,
    filterModal: false,
    filterState: initialFilterState,
    currentItinSeq: 0,
    isStorageUsed: false,
    setActiveTab: emptyFunc,
    setFilterModal: emptyFunc,
    setCurItinSeq: emptyFunc,
    updateSelected: emptyFunc,
    setFilterState: emptyFunc,
    setFilterStates: emptyFunc,
    filterStore: initialFilterStore,
    setFilterStore: emptyFunc,
  });

interface IProps {
  children: React.ReactNode;
  queryVariables: IQueryValue;
  itineraryIataDetails: IAirportDetail[];
}

export function InternationalFilterContextProvider({
  children,
  queryVariables,
  itineraryIataDetails,
}: IProps) {
  const {
    trip,
    itinerary,
    adult,
    child,
    infant,
    fareType,
    isDirect,
    stayLength,
  } = queryVariables;
  const [filterStore, setFilterStore] = useState<IFilterStore>(
    loadFilterStore(
      itinerary,
      trip,
      itineraryIataDetails,
      isDirect,
      stayLength,
    ),
  );
  const {
    airlineFilter,
    airportFilter,
    carbonEmissionOnlyFilter,
    departureTimeFilters,
    durationFilters,
    excludeCardFilter,
    fareTypeFilter,
    isStorageUsed,
    priceFilter,
    sameAirlineOnlyFilter,
    sameAirportOnlyFilter,
    travelClubOnlyFilter,
    viaCountFilter,
  } = filterStore;
  const stateItinerary = JSON.stringify(itinerary);
  const [activeTab, setActiveTab] = useState<
    InternationalFilterTab | undefined
  >();
  const [filterModal, setFilterModal] = useState(false);
  const [filterState, setFilterState] = useState(initialFilterState);
  const [currentItinSeq, setCurItinSeq] = useState<number>(0);

  const setState = (key: FilterKeys, val: any) => {
    const newState = { ...filterState, [key]: val };
    setFilterState(newState);
  };

  const setStates = (states: { [key: string]: any }) => {
    const newState = { ...filterState, ...states };
    setFilterState(newState);
  };

  const updateSelected = () => {
    const newState = {
      ...filterState,
      [FilterKeys.airline]: airlineFilter.getSelectedSet(),
      [FilterKeys.airport]: airportFilter.map(filters =>
        filters.map(filter => filter.getSelectedSet()),
      ),
      [FilterKeys.carbonEmissionOnly]: carbonEmissionOnlyFilter,
      [FilterKeys.departureTime]: departureTimeFilters.map(
        (el: any) => el.getSelectedSet() || new Set<DepartureTime>([]),
      ),
      [FilterKeys.duration]: durationFilters.map(
        (el: any) =>
          new RangeFilter(el.getFrom(), el.getTo(), el.getMin(), el.getMax()),
      ),
      [FilterKeys.excludeCard]: excludeCardFilter,
      [FilterKeys.fareType]: fareTypeFilter.getSelectedSet(),
      [FilterKeys.price]: new RangeFilter(
        priceFilter.getFrom(),
        priceFilter.getTo(),
        priceFilter.getMin(),
        priceFilter.getMax(),
      ),
      [FilterKeys.sameAirlineOnly]: sameAirlineOnlyFilter,
      [FilterKeys.sameAirportOnly]: sameAirportOnlyFilter,
      [FilterKeys.travelClubOnly]: travelClubOnlyFilter,
      [FilterKeys.viaCount]: viaCountFilter.getSelectedSet(),
    };
    setFilterState(newState);
  };

  useEffect(() => {
    updateSelected();
  }, [
    airlineFilter,
    airportFilter,
    carbonEmissionOnlyFilter,
    departureTimeFilters,
    durationFilters,
    excludeCardFilter,
    fareTypeFilter,
    priceFilter,
    sameAirlineOnlyFilter,
    sameAirportOnlyFilter,
    travelClubOnlyFilter,
    viaCountFilter,
  ]);

  useEffect(() => {
    setFilterStore(
      loadFilterStore(
        itinerary,
        trip,
        itineraryIataDetails,
        isDirect,
        stayLength,
      ),
    );
  }, [
    trip,
    stateItinerary,
    adult,
    child,
    infant,
    fareType,
    isDirect,
    stayLength,
  ]);

  return (
    <InternationalFilterContext.Provider
      value={{
        activeTab,
        filterModal,
        filterState,
        setFilterState: setState,
        setFilterStates: setStates,
        currentItinSeq,
        isStorageUsed,
        setActiveTab,
        setFilterModal,
        setCurItinSeq,
        updateSelected,
        filterStore,
        setFilterStore,
      }}
    >
      {children}
    </InternationalFilterContext.Provider>
  );
}
